/**
 * The Overview on the org channel (`org:{orgId}`, OrgRoom; §2.11 "Live data", §2.15, screenshots/90):
 * - `layout.saved` (an admin published the team layout): when I'm looking at the team layout and not editing, it's
 *   fetched and swapped in (layoutStore.remote); while I'm editing, my next save gets a 409 and merges instead.
 * - `theme.saved` / `theme.deleted`: themes are reloaded so open charts switch with setTheme.
 * - `presence`: "Jordan is also editing · moved 2 widgets" + "Jordan moved this" tags. I send my own presence while
 *   customizing (the widgets I've touched since I started), and `editing: null` when I'm done.
 * - Analytics (screenshots/84, 90): on a dashboard the grid's host is "dashboard:<id>", presence is sent and matched with that
 *   key, and `dashboard.saved` by someone else swaps their layout in when I'm not editing (else my next save merges).
 * - `entitlements.changed` (staff changed this org's features): the viewer's feature map is refetched and the nav and
 *   gated views re-render (realtime/entitlements.ts). For that, every team page opens this channel (main.tsx), quietly
 *   (no connection status) outside the Overview and Analytics.
 * - `stance.set` / `positions.saved` / `targets.set` (the team's positions): realtime/teamLive.ts applies them to the
 *   People and Legislation stores.
 * Opens after first paint, like every channel (LiveChannel: hello/lastSeq, backoff, connectionStore).
 */
import { LiveChannel } from "../realtime/liveClient";
import { onEntitlementsDelta } from "../realtime/entitlements";
import { onTeamDelta } from "../realtime/teamLive";
import type { OverviewOrgDelta } from "../realtime/protocol";
import { loadThemes } from "./chartEditorStore";
import { normalizeItems, overviewLayout, overviewStore } from "./overviewStore";
import type { OverviewSnapshot } from "./types";

let channel: LiveChannel | null = null;
let seq = 0;

async function refreshTeamLayout() {
  const r = await fetch("/api/me/overview", { credentials: "include" }).catch(() => null);
  if (!r?.ok) return;
  const d = (await r.json()) as OverviewSnapshot["layouts"];
  const s = overviewStore.getState();
  if (!s.snapshot) return;
  overviewStore.setState({ snapshot: { ...s.snapshot, layouts: { ...s.snapshot.layouts, team: d.team } } });
  if (s.scope === "team" && d.team) overviewLayout.getState().remote(normalizeItems(d.team.items), d.team.version ?? 0);
}

/** Someone else saved the dashboard I have open: take their layout unless I'm editing (then my next save merges). */
async function refreshDashboard(id: string, version: number) {
  if (version <= overviewLayout.getState().version) return;
  const r = await fetch(`/api/dashboards/${encodeURIComponent(id)}`, { credentials: "include" }).catch(() => null);
  if (!r?.ok || overviewStore.getState().host !== `dashboard:${id}`) return;
  const d = (await r.json()) as { layout: unknown[]; version: number };
  overviewLayout.getState().remote(normalizeItems(d.layout), d.version);
}

function onDelta(n: number, d: OverviewOrgDelta) {
  seq = Math.max(seq, n);
  const host = overviewStore.getState().host;
  if (d.t === "layout.saved") { if (host === "overview") void refreshTeamLayout(); }
  else if (d.t === "theme.saved" || d.t === "theme.deleted") loadThemes(undefined, undefined, true);
  else if (d.t === "presence") overviewStore.setState({ presence: d.editing === host ? { name: d.name, touched: d.touched } : null });
  else if (d.t === "dashboard.saved" && host === `dashboard:${d.id}`) void refreshDashboard(d.id, d.version);
  else if (d.t === "entitlements.changed") void onEntitlementsDelta(d);
  // Casework and Campaigns (design section 11): their pages refetch (casework.changed, campaign.changed, campaign.action).
  else if (/^(casework|campaign)\./.test((d as { t: string }).t)) dispatchEvent(new CustomEvent("team:work", { detail: d }));
  // A teammate annotated a document (worker/src/annotations.js): an open PDF viewer of that document refetches.
  else if ((d as { t: string }).t === "annotations.changed") dispatchEvent(new CustomEvent("team:annotations", { detail: d }));
  else onTeamDelta(d);   // the team's stances on bills, positions, target lists (realtime/teamLive.ts)
}

export function bootOverviewLive(orgId: string, { quiet = false }: { quiet?: boolean } = {}) {
  if (channel || typeof WebSocket === "undefined") return;
  channel = new LiveChannel({
    quiet,
    url: `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/live/org/${encodeURIComponent(orgId)}`,
    channel: `org:${orgId}`,
    getLastSeq: () => seq,
    onSnapshot: () => {},
    onDelta: (n, d) => onDelta(n, d as unknown as OverviewOrgDelta),
  });
  // My presence while customizing (sent on start, on every change of what I've touched, and on Done).
  overviewLayout.subscribe((s) => (s.customizing ? s.touched.join(",") : null), (touched) => {
    channel?.send({ type: "presence", editing: touched === null ? null : overviewStore.getState().host, touched: touched ? touched.split(",").filter(Boolean) : [] });
  });
  const start = () => channel?.start();
  if ("requestIdleCallback" in window) requestIdleCallback(start, { timeout: 1500 }); else setTimeout(start, 300);
}
